import React, { FC, useMemo, useCallback, useState, useEffect, useRef, MouseEvent } from 'react';
import { generateColumn } from 'src/common/components/table/LegacyTable';

import { Node } from 'src/common/interfaces/node.interface';
import { formatDateTime } from 'src/utils/date';
import { getNumOfNodes, getAllNodesWithSteps, getTotalEngaments } from 'src/utils/journey';

import { DashboardHomeJourneyActions } from './actions';
import { DashboardHomeJourneysExpanded } from './expanded';
import { Journey } from 'src/common/interfaces/journey.interface';
import {
  TableSortableColumnHeader,
  TableSortableColumnHeaderSortType,
} from 'src/common/components/table/sortable-column-header';
import { DataTable } from 'src/common/components/table/data-table';
import { useHistory } from 'react-router-dom';
import { Step } from 'src/common/interfaces/step.interface';
import { useEditorContext } from 'src/store/editor';
import LoadingSpinner from 'src/common/components/loading-spinner';
import { apiGetJourney } from 'src/utils/journeyApi';
import { formatNumber } from 'src/utils/text';
import { TooltipComponent } from 'src/common/tooltip/component';
import { Checkbox } from 'src/common/form/checkbox';
import { useCurrentSubscription } from 'src/store/subscription';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';
import { isJourneyAccessible } from 'src/utils/journey-access';
import { LockClosedIcon } from '@heroicons/react/outline';
import classNames from 'classnames';
import { getJourneyAccessInfo } from 'src/utils/journey-access';

export type DashboardHomeJourneysListHeaderTypes =
  | 'name'
  | 'engagements'
  | 'steps'
  | 'creator'
  | 'created_at'
  | 'updated_at'
  | 'workspace'
  | 'tags'
  | 'status';

export type DashboardHomeJourneyListHeaderSortType =
  TableSortableColumnHeaderSortType<DashboardHomeJourneysListHeaderTypes>;

export interface DashboardHomeJourneysListProps {
  journeys: Journey[];
  searchInputValue: string;
  activeTableSortType: DashboardHomeJourneyListHeaderSortType;
  isListEmpty: boolean;
  onTableHeaderClick: (stack: DashboardHomeJourneyListHeaderSortType) => void;
  selectedRows: Set<string>;
  setSelectedRows: React.Dispatch<React.SetStateAction<Set<string>>>;
}

export const DashboardHomeJourneysList: FC<DashboardHomeJourneysListProps> = ({
  journeys,
  searchInputValue,
  activeTableSortType,
  onTableHeaderClick,
  isListEmpty,
  selectedRows,
  setSelectedRows,
}) => {
  interface Tag {
    name: string;
  }

  type JourneyUUID = string;

  type TagsObject = {
    tags?: Tag[];
  };

  type TagsState = {
    [key: JourneyUUID]: TagsObject;
  };

  const [loadingExpandedJourney, setLoadingExpandedJourney] = useState(false);

  const history = useHistory();
  const { setJourney, journey: expandedJourney } = useEditorContext();
  const { subscriptionPlanType, isOnTrial } = useCurrentSubscription();
  const { showUpgradeModal } = useFeatureRestrictions();

  // Get journey access information
  const journeyAccessInfo = getJourneyAccessInfo(journeys, subscriptionPlanType, isOnTrial);

  const formatDate = (datetime: any) => (datetime ? formatDateTime(datetime) : '-');

  const getAllNodesWithStepsByIndex = useCallback(
    (journey: Journey): { node: Node; step: Step }[] => getAllNodesWithSteps(journey),
    []
  );

  const selectedRowsRef = useRef(selectedRows);

  useEffect(() => {
    selectedRowsRef.current = selectedRows;
  }, [selectedRows]);

  const toggleRowSelection = (journeyUuid: string) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(journeyUuid)) {
        newSet.delete(journeyUuid);
      } else {
        newSet.add(journeyUuid);
      }
      return newSet;
    });
  };

  const handleHeaderSelectAllRowsChange = (data: any) => {
    if (selectedRowsRef.current.size === data.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(journeys.map((c) => c.uuid)));
    }
  };

  const stopPropagation = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };
  const columns = useMemo(
    () => [
      generateColumn({
        Header: ({ data }: { data: any }) => {
          return (
            <Checkbox
              isBedrock
              checked={data.length > 0 && selectedRowsRef.current.size === data.length}
              onChange={() => handleHeaderSelectAllRowsChange(data)}
            />
          );
        },
        accessor: 'select',
        width: 40,
        Cell: (data: any) => {
          return (
            <div onClick={stopPropagation} className='relative'>
              <Checkbox
                isBedrock
                className='mt-0.5'
                checked={selectedRowsRef.current.has(data.row.original.uuid)}
                onChange={() => toggleRowSelection(data.row.original.uuid)}
              />
            </div>
          );
        },
      }),

      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={activeTableSortType.sortName === 'name'}
            activeOrderType={activeTableSortType.orderBy}
            accessor='name'
            onClick={onTableHeaderClick}
            title='Name'
          />
        ),
        accessor: 'name',
        width: '32%',
        allowShrinking: true,
        Cell: ({ index }: { index: number }) => {
          const journey = journeys[index];
          const isAccessible = isJourneyAccessible(journey, journeys, subscriptionPlanType, isOnTrial);
          
          return (
            <div className='flex items-center space-x-2'>
              <span className={classNames('line-clamp-2 lg:line-clamp-none', {
                'opacity-60': !isAccessible,
              })}>
                {journey.name}
              </span>
              {!isAccessible && (
                <TooltipComponent 
                  placement='top' 
                  tooltipContent='Upgrade to Grow plan to access this journey'
                >
                  <LockClosedIcon className='w-4 h-4 text-gray-400 flex-shrink-0' />
                </TooltipComponent>
              )}
            </div>
          );
        },
      }),
      generateColumn({
        Header: 'Total Views',
        accessor: 'engagements',
        width: '11%',
        allowShrinking: true,
        Cell: ({ index }: { index: number }) => {
          return formatNumber(getTotalEngaments(journeys[index]));
        },
      }),
      generateColumn({
        Header: 'Steps',
        accessor: 'steps',
        width: '6%',
        allowShrinking: false,
        Cell: ({ index }: { index: number }) => formatNumber(getNumOfNodes(journeys[index])),
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={activeTableSortType.sortName === 'creator'}
            activeOrderType={activeTableSortType.orderBy}
            accessor='creator'
            onClick={onTableHeaderClick}
            title='Creator'
          />
        ),
        accessor: 'creator',
        width: '13%',
        allowShrinking: true,
        Cell: ({ index }: { index: number }) => (
          <span className='text-bedrock-p text-bedrock-black line-clamp-2 lg:line-clamp-none'>
            {journeys[index].creator?.name}
          </span>
        ),
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={activeTableSortType.sortName === 'created_at'}
            activeOrderType={activeTableSortType.orderBy}
            accessor='created_at'
            onClick={onTableHeaderClick}
            title='Created'
          />
        ),
        accessor: 'created_at',
        width: '8%',
        allowShrinking: false,
        Cell: ({ index }: { index: number }) => (
          <span className='text-bedrock-p text-bedrock-black'>{formatDate(journeys[index].created_at)}</span>
        ),
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={activeTableSortType.sortName === 'updated_at'}
            activeOrderType={activeTableSortType.orderBy}
            accessor='updated_at'
            onClick={onTableHeaderClick}
            title='Edited'
          />
        ),
        accessor: 'updated_at',
        width: '8%',
        Cell: ({ index }: { index: number }) => (
          <span className='text-bedrock-p text-bedrock-black'>{formatDate(journeys[index].updated_at)}</span>
        ),
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={activeTableSortType.sortName === 'workspace'}
            activeOrderType={activeTableSortType.orderBy}
            accessor='workspace'
            onClick={onTableHeaderClick}
            title='Workspace'
          />
        ),
        accessor: 'workspace',
        width: '13%',
        allowShrinking: false,
        Cell: ({ row }: { row: any }) => (
          <span className='text-bedrock-p text-bedrock-black'>{(row.original as Journey).workspace.name}</span>
        ),
      }),
      generateColumn({
        Header: 'Tags',
        accessor: 'tags',
        width: '13%',
        allowShrinking: false,
        Cell: ({ index }: { index: number }) => {
          const tags = journeys[index]?.tags || [];
          const displayedTags = tags.slice(0, 2);
          const moreTagsCount = tags.length - 2;
          const moreTags = moreTagsCount > 0 ? tags.slice(2) : [];

          return (
            <div className='flex flex-row gap-2'>
              {displayedTags.map((tag) => (
                <span
                  key={tag.id}
                  className='text-black block px-2 py-0.5 w-fit rounded border-2 border-bedrock-gray-medium bg-neue-canvas'
                >
                  {tag.name}
                </span>
              ))}
              {moreTagsCount > 0 && (
                <TooltipComponent
                  tooltipContent={
                    <>
                      {moreTags.map((tag) => (
                        <div key={tag.id}>{tag.name}</div>
                      ))}
                    </>
                  }
                  placement='bottom'
                  styling='tooltip'
                  theme='journey'
                >
                  <span className='text-black block px-2 py-0.5 w-fit'>+{moreTagsCount}</span>
                </TooltipComponent>
              )}
            </div>
          );
        },
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={activeTableSortType.sortName === 'status'}
            activeOrderType={activeTableSortType.orderBy}
            accessor='status'
            onClick={onTableHeaderClick}
            title='Status'
          />
        ),
        accessor: 'status',
        width: '13%',
        allowShrinking: false,
        Cell: ({ row }: { row: any }) => (
          <span className='text-bedrock-p text-bedrock-black'>
            {(row.original as Journey).status === 'public' ? 'Published' : 'Draft'}
          </span>
        ),
      }),
      generateColumn({
        Header: '',
        accessor: 'actions',
        width: '7%',
        allowShrinking: true,
        Cell: ({ row }: { row: any }) => {
          return (
            <div className='text-bedrock-p text-bedrock-black mr-2'>
              <div className='flex grow-0 justify-end'>
                <DashboardHomeJourneyActions
                  journey={row.original as Journey}
                  rowExpanderProps={row.getToggleRowExpandedProps()}
                  expanded={row.isExpanded}
                />
              </div>
            </div>
          );
        },
      }),
    ],
    [
      activeTableSortType.sortName,
      activeTableSortType.orderBy,
      journeys,
      onTableHeaderClick,
      handleHeaderSelectAllRowsChange,
      toggleRowSelection,
      subscriptionPlanType,
      isOnTrial,
    ]
  );

  const renderRowSubComponent = useCallback(
    ({ index }) => {
      return (
        <>
          {loadingExpandedJourney ? (
            <LoadingSpinner />
          ) : (
            <DashboardHomeJourneysExpanded nodes={getAllNodesWithStepsByIndex(expandedJourney || journeys[index])} />
          )}
        </>
      );
    },
    [expandedJourney, journeys, getAllNodesWithStepsByIndex, loadingExpandedJourney]
  );

  const onRowClicked = (row: unknown, index: number) => {
    const journey = journeys[index];
    
    // Check if journey is accessible for the current user
    if (!isJourneyAccessible(journey, journeys, subscriptionPlanType, isOnTrial)) {
      showUpgradeModal(
        'unlimited_journeys',
        'You have reached your journey limit on the Start plan. Upgrade to Grow plan to access all your journeys and create unlimited new ones.',
        'Upgrade to Access All Journeys'
      );
      return;
    }
    
    const isNeue = journey?.data?.neue;
    if (isNeue) {
      history.push(`/journeys/neue-edit/${journey.uuid}`);
    } else {
      history.push(`/journeys/edit/${journey.uuid}`);
    }
  };

  if (searchInputValue && isListEmpty) {
    return <span className='text-bedrock-p text-bedrock-black'>Sorry, no results for "{searchInputValue}"</span>;
  }

  const onTableRowExpanded = async (row?: { original: unknown }) => {
    if (row) {
      setLoadingExpandedJourney(true);
      try {
        const journey = await apiGetJourney((row.original as Journey).uuid);
        setJourney(journey);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingExpandedJourney(false);
      }
    } else {
      setJourney(null);
    }
  };

  return (
    <div>
      {/* Journey restriction banner */}
      {journeyAccessInfo.hasRestrictedJourneys && (
        <div className='mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg'>
          <div className='flex items-start'>
            <LockClosedIcon className='w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0' />
            <div className='flex-1'>
              <h3 className='text-sm font-medium text-blue-900 mb-1'>
                Journey Access Limited
              </h3>
              <p className='text-sm text-blue-700 mb-3'>
                You have {journeys.length} journeys but your Start plan only allows access to the first {journeyAccessInfo.accessibleJourneys.length}. 
                You can still delete any journey, but other operations require upgrading to Grow plan.
              </p>
              <button
                onClick={() => showUpgradeModal(
                  'unlimited_journeys',
                  'Upgrade to Grow plan to access all your journeys and create unlimited new ones.',
                  'Upgrade to Access All Journeys'
                )}
                className='inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              >
                Upgrade to Grow Plan
              </button>
            </div>
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        data={journeys}
        columnClasses={['', '', '', 'break-words', '', '', '', '']}
        onRowClick={onRowClicked}
        renderRowSubComponent={renderRowSubComponent}
        expandOnlySingleRow
        disableSubComponentStyle
        disableCheckDataOnExpand
        onToggleExpandedRow={onTableRowExpanded}
      />
    </div>
  );
};
